import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, mobileAppSchema, BRAND } from './SEOUtils';

export default function BlackPolygamyDatingPage() {
  const schemas = [organizationSchema, mobileAppSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Black Polygamy Dating App | Plural Marriage Dating | UnityLove"
        description="UnityLove supports Black polygamy dating for plural families—while respecting polyamory and monogamy. Built for structured multi-partner commitment."
        canonical="/black-polygamy-dating"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Black Polygamy Dating for Those Building Plural Families
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            Designed for Black polygamous families and those exploring structured multi-partner commitment—while respecting polyamory and monogamy when practiced with clarity and consent.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            UnityLove is Black poly-centered: we welcome polygamy, polyamory, and intentional monogamy—all paths grounded in honesty and respect.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Join UnityLove Today
            </Link>
            <a
              href={BRAND.appStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Download App
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Understanding Black Polygamy Dating
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Polygamy—having multiple spouses—has deep roots in African and African-American history. For many Black individuals today, exploring polygamous relationships is about reconnecting with ancestral practices, building strong family units, and creating relationship structures that serve their values.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove provides a platform for Black polygamy dating that respects these traditions while centering consent, communication, and mutual care. Whether you're a man seeking sister-wives, a woman interested in joining a polygamous family, or an existing plural family looking to expand, you'll find understanding community members here.
          </p>
          <p className="text-lg text-slate-700">
            We recognize that polygamy means different things to different people. Some approach it from religious conviction, others from cultural heritage, and others simply because it aligns with their vision of family. All are welcome on UnityLove.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Polygamy in African and Black American Culture
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Across the African continent, polygyny (a man having multiple wives) has been practiced for millennia. These weren't arrangements of oppression but of community—women in polygynous marriages often gained extended family support, shared domestic responsibilities, and strong sisterhood bonds with co-wives.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            In America, Black communities have a complex history with plural relationships, from the forced separations of slavery to the reclamation movements of today. Modern Black polygamy often blends African traditions with contemporary values of equality and choice.
          </p>
          <p className="text-lg text-slate-700">
            UnityLove honors this history while supporting members in creating polygamous relationships that work for modern life—relationships built on love, respect, and shared purpose.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            How UnityLove Supports Polygamous Dating
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Family Profile Options</h3>
              <p className="text-slate-700">Create profiles that represent your existing family structure. Potential partners can understand what they'd be joining from the start.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Targeted Matching</h3>
              <p className="text-slate-700">Our system connects you with members specifically interested in polygamous relationships—no more filtering through incompatible matches.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Cultural Understanding</h3>
              <p className="text-slate-700">Connect with others who share your cultural background and understand the significance of polygamy in Black traditions.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Safe Conversation Space</h3>
              <p className="text-slate-700">Discuss relationship expectations, family dynamics, and logistics openly with potential partners who get it.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Polygamy vs. Polyamory: What's the Difference?
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            While both involve multiple partners, they differ in structure:
          </p>
          <ul className="list-disc list-inside space-y-2 text-lg text-slate-700 mb-4">
            <li><strong>Polygamy</strong> typically involves marriage (or marriage-like commitment) to multiple spouses, often with a specific structure (like one husband with multiple wives).</li>
            <li><strong>Polyamory</strong> focuses on multiple romantic/loving relationships that may or may not involve marriage or specific hierarchy.</li>
          </ul>
          <p className="text-lg text-slate-700">
            UnityLove supports both. If you're more interested in the polyamorous approach, explore our <Link to="/black-polyamory-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black polyamory dating</Link> community. Many members find value in both frameworks.
          </p>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Build Your Plural Family</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Whether you're starting your polygamous journey or expanding an existing family, UnityLove connects you with like-minded Black individuals.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
          >
            Create Your Profile
          </Link>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Related Topics</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black Poly Dating</Link>
            <Link to="/black-polyamory-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Polyamory Dating</Link>
            <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">Ethical Non-Monogamy</Link>
            <Link to="/black-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Dating App</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. Black polygamy dating for intentional family building.</p>
        </div>
      </footer>
    </div>
  );
}
